// The only module that loads or calls Google Maps (VITE_GOOGLE_MAPS_KEY).
import { placeLevelFromTypes, type PlaceLevel } from '@guildite/domain/delivery';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';

import { AREA_CIRCLE_RADIUS_METERS, areaFromComponents, townFromComponents, directionsUrl, type MapHandle, type MapItem, type MapMarker, type MapShape, type MapsPort, type ShapeEvents } from '../index.ts';

const ROYAL_BLUE = '#1E56D8';
const ORANGE = '#C2410C';

// Boundaries Google can highlight for a place (data-driven styling). They
// need a map ID with these feature layers on; otherwise the place's area is
// drawn as its viewport rectangle.
const BOUNDARY_LAYERS: Partial<Record<PlaceLevel, string>> = {
  country: 'COUNTRY', region: 'ADMINISTRATIVE_AREA_LEVEL_1', district: 'ADMINISTRATIVE_AREA_LEVEL_2', town: 'LOCALITY',
};

// Google never answers when its script is blocked or the network drops; after
// this long the map counts as failed (V2-051).
const LOAD_TIMEOUT_MS = 15_000;

export function createGoogleMaps({ apiKey, mapId }: { apiKey: string; mapId: string }): MapsPort {
  let configured = false;
  // Google calls window.gm_authFailure when it refuses the key for this site.
  let refused = false;
  const refusedListeners = new Set<() => void>();

  async function libraries() {
    if (refused) throw new Error('Google Maps refused this site.');
    if (!configured) {
      setOptions({ key: apiKey, v: 'weekly' });
      (window as Window & { gm_authFailure?: () => void }).gm_authFailure = () => {
        refused = true;
        refusedListeners.forEach(listener => listener());
      };
      configured = true;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('Google Maps did not load.')), LOAD_TIMEOUT_MS); });
    try {
      const [maps, marker, core] = await Promise.race([Promise.all([importLibrary('maps'), importLibrary('marker'), importLibrary('core')]), timeout]);
      return { maps, marker, core };
    } finally {
      clearTimeout(timer);
    }
  }

  return {
    available: Boolean(apiKey),

    async mount(element, { center, zoom, interactive, label, colorScheme, controls = true, onFail }): Promise<MapHandle> {
      const { maps, marker, core } = await libraries();
      if (onFail) refusedListeners.add(onFail);
      const map = new maps.Map(element, {
        center, zoom, mapId,
        // Google's own dark styling follows the app theme; the map is re-created when it changes.
        colorScheme: colorScheme === 'dark' ? core.ColorScheme.DARK : core.ColorScheme.LIGHT,
        disableDefaultUI: true,
        zoomControl: controls,
        clickableIcons: false,
        keyboardShortcuts: interactive,
        gestureHandling: interactive ? 'greedy' : 'cooperative',
      });
      element.setAttribute('aria-label', label);
      let pin: google.maps.marker.AdvancedMarkerElement | null = null;
      let circle: google.maps.Circle | null = null;
      let rings: google.maps.Circle[] = [];
      let many: { remove(): void }[] = [];
      const pickListeners = new Set<(point: google.maps.LatLngLiteral) => void>();
      // showShapes keeps its shapes by id so a circle being dragged survives a redraw.
      const shapes = new Map<string, { shape: MapShape; remove(): void; update(next: MapShape): void }>();
      let shapeEvents: ShapeEvents = {};
      const styledLayers = new Set<string>();

      function highlightPlaces(selected: MapShape[]) {
        const byLayer = new Map<string, Map<string, boolean>>();
        for (const shape of selected) {
          if (shape.kind !== 'place' || !shape.placeId) continue;
          const layer = BOUNDARY_LAYERS[shape.level];
          if (!layer) continue;
          if (!byLayer.has(layer)) byLayer.set(layer, new Map());
          byLayer.get(layer)!.set(shape.placeId, shape.selected);
        }
        let drawn = new Set<string>();
        // Asking for layers on a map style without them logs errors, so only
        // ask when there is a place to outline and the style supports it.
        if ((!byLayer.size && !styledLayers.size) || !map.getMapCapabilities().isDataDrivenStylingAvailable) return drawn;
        for (const layerName of new Set([...byLayer.keys(), ...styledLayers])) {
          try {
            const layer = map.getFeatureLayer(layerName as google.maps.FeatureType);
            if (!layer.isAvailable) continue;
            const ids = byLayer.get(layerName) ?? new Map<string, boolean>();
            layer.style = ids.size ? (options => {
              const placeId = (options.feature as google.maps.PlaceFeature).placeId;
              if (!ids.has(placeId)) return null;
              return { strokeColor: ids.get(placeId) ? ORANGE : ROYAL_BLUE, strokeWeight: ids.get(placeId) ? 3 : 2, fillColor: ROYAL_BLUE, fillOpacity: 0.12 };
            }) : null;
            if (ids.size) styledLayers.add(layerName);
            else styledLayers.delete(layerName);
            drawn = new Set([...drawn, ...ids.keys()]);
          } catch {
            // No data-driven styling on this map: rectangles below.
          }
        }
        return drawn;
      }

      function drawShape(shape: MapShape, boundaries: Set<string>) {
        if (shape.kind === 'pin') {
          const element = new marker.AdvancedMarkerElement({ map, position: shape.point, title: shape.label });
          return { remove: () => { element.map = null; }, update: (next: MapShape) => { if (next.kind === 'pin') element.position = next.point; } };
        }
        if (shape.kind === 'place') {
          if (shape.placeId && boundaries.has(shape.placeId)) return { remove: () => {}, update: () => {} };
          const rectangle = shape.viewport ? new maps.Rectangle({
            map, bounds: shape.viewport, clickable: true,
            strokeColor: shape.selected ? ORANGE : ROYAL_BLUE, strokeWeight: 2, fillColor: ROYAL_BLUE, fillOpacity: 0.08,
          }) : null;
          const listener = rectangle?.addListener('click', () => shapeEvents.onSelect?.(shape.id));
          return {
            remove: () => { listener?.remove(); rectangle?.setMap(null); },
            update: (next: MapShape) => { if (next.kind === 'place') rectangle?.setOptions({ strokeColor: next.selected ? ORANGE : ROYAL_BLUE }); },
          };
        }
        const circleShape = new maps.Circle({
          map, center: shape.center, radius: shape.radius, clickable: true, editable: shape.editable, draggable: shape.editable,
          strokeColor: shape.selected ? ORANGE : ROYAL_BLUE, strokeOpacity: 0.9, strokeWeight: shape.selected ? 3 : 2, fillColor: ROYAL_BLUE, fillOpacity: 0.12,
        });
        // A drag fires many changes; report once it settles.
        let timer: ReturnType<typeof setTimeout> | undefined;
        const report = () => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            const center = circleShape.getCenter();
            if (center) shapeEvents.onCircleChange?.(shape.id, { lat: center.lat(), lng: center.lng() }, Math.round(circleShape.getRadius()));
          }, 150);
        };
        const listeners = [
          circleShape.addListener('center_changed', report), circleShape.addListener('radius_changed', report),
          circleShape.addListener('click', () => shapeEvents.onSelect?.(shape.id)),
        ];
        return {
          remove: () => { clearTimeout(timer); listeners.forEach(item => item.remove()); circleShape.setMap(null); },
          update: (next: MapShape) => {
            if (next.kind !== 'circle') return;
            const center = circleShape.getCenter();
            if (!center || Math.abs(center.lat() - next.center.lat) > 1e-6 || Math.abs(center.lng() - next.center.lng) > 1e-6) circleShape.setCenter(next.center);
            if (Math.abs(circleShape.getRadius() - next.radius) > 1) circleShape.setRadius(next.radius);
            circleShape.setOptions({ editable: next.editable, draggable: next.editable, strokeColor: next.selected ? ORANGE : ROYAL_BLUE, strokeWeight: next.selected ? 3 : 2 });
          },
        };
      }

      function clear() {
        if (pin) pin.map = null;
        circle?.setMap(null);
        rings.forEach(ring => ring.setMap(null));
        pin = null;
        circle = null;
        rings = [];
        many.forEach(item => item.remove());
        many = [];
      }

      function areaCircle(center: google.maps.LatLngLiteral, clickable: boolean) {
        return new maps.Circle({
          map, center, radius: AREA_CIRCLE_RADIUS_METERS, clickable,
          strokeColor: ROYAL_BLUE, strokeOpacity: 0.8, strokeWeight: 2, fillColor: ROYAL_BLUE, fillOpacity: 0.12,
        });
      }

      return {
        show(next: MapMarker | null) {
          clear();
          if (!next) return;
          if (next.kind === 'pin') {
            pin = new marker.AdvancedMarkerElement({ map, position: next.point, title: next.label, gmpDraggable: next.draggable === true });
            if (next.draggable) {
              // Dropping the pin chooses that point, as a tap does.
              pin.addListener('dragend', () => {
                const position = pin?.position;
                if (!position) return;
                const point = 'lat' in position && typeof position.lat === 'function'
                  ? { lat: (position as google.maps.LatLng).lat(), lng: (position as google.maps.LatLng).lng() }
                  : position as google.maps.LatLngLiteral;
                pickListeners.forEach(listener => listener(point));
              });
            }
            map.panTo(next.point);
            return;
          }
          if (next.kind === 'rings') {
            // Inner rings are drawn darker, so each band reads on its own.
            const radii = [...next.radii].sort((a, b) => b - a);
            rings = radii.map((radius, index) => new maps.Circle({
              map, center: next.center, radius, clickable: false,
              strokeColor: ROYAL_BLUE, strokeOpacity: 0.8, strokeWeight: 2, fillColor: ROYAL_BLUE, fillOpacity: 0.06 + index * 0.05,
            }));
            const outer = rings[0]?.getBounds();
            if (outer) map.fitBounds(outer, 24);
            return;
          }
          circle = areaCircle(next.center, false);
          const bounds = circle.getBounds();
          if (bounds) map.fitBounds(bounds, 24);
        },
        showShapes(next, events, { fit = false } = {}) {
          shapeEvents = events;
          const boundaries = highlightPlaces(next);
          const ids = new Set(next.map(shape => shape.id));
          for (const [id, drawn] of shapes) {
            if (!ids.has(id)) { drawn.remove(); shapes.delete(id); }
          }
          for (const shape of next) {
            const drawn = shapes.get(shape.id);
            if (drawn && drawn.shape.kind === shape.kind) {
              drawn.update(shape);
              drawn.shape = shape;
            } else {
              drawn?.remove();
              shapes.set(shape.id, { shape, ...drawShape(shape, boundaries) });
            }
          }
          if (!fit || !next.length) return;
          const bounds = new core.LatLngBounds();
          for (const shape of next) {
            if (shape.kind === 'pin') bounds.extend(shape.point);
            else if (shape.kind === 'circle') bounds.union(new maps.Circle({ center: shape.center, radius: shape.radius }).getBounds() ?? new core.LatLngBounds(shape.center, shape.center));
            else if (shape.viewport) bounds.union(new core.LatLngBounds({ lat: shape.viewport.south, lng: shape.viewport.west }, { lat: shape.viewport.north, lng: shape.viewport.east }));
            else if (shape.center) bounds.extend(shape.center);
          }
          if (!bounds.isEmpty()) map.fitBounds(bounds, 32);
        },
        showMany(items: MapItem[], onSelect) {
          clear();
          const bounds = new core.LatLngBounds();
          for (const { id, marker: item } of items) {
            if (item.kind === 'pin') {
              const element = new marker.AdvancedMarkerElement({ map, position: item.point, title: item.label, gmpClickable: true });
              const listener = element.addListener('gmp-click', () => onSelect(id));
              many.push({ remove: () => { listener.remove(); element.map = null; } });
              bounds.extend(item.point);
            } else {
              const shape = areaCircle(item.center, true);
              const listener = shape.addListener('click', () => onSelect(id));
              many.push({ remove: () => { listener.remove(); shape.setMap(null); } });
              bounds.union(shape.getBounds() ?? new core.LatLngBounds(item.center, item.center));
            }
          }
          const only = items.length === 1 ? items[0]?.marker : null;
          if (only?.kind === 'pin') {
            map.setCenter(only.point);
            map.setZoom(15);
          } else if (items.length) {
            map.fitBounds(bounds, 32);
          }
        },
        focus(point, zoom = 15) {
          map.panTo(point);
          map.setZoom(zoom);
        },
        zoomBy(delta) {
          map.setZoom((map.getZoom() ?? 13) + delta);
        },
        onPick(listener) {
          const subscription = map.addListener('click', (event: google.maps.MapMouseEvent) => {
            if (event.latLng) listener({ lat: event.latLng.lat(), lng: event.latLng.lng() });
          });
          pickListeners.add(listener);
          return () => { subscription.remove(); pickListeners.delete(listener); };
        },
        center() {
          const value = map.getCenter();
          return value ? { lat: value.lat(), lng: value.lng() } : null;
        },
        destroy() {
          if (onFail) refusedListeners.delete(onFail);
          clear();
          shapes.forEach(drawn => drawn.remove());
          shapes.clear();
          google.maps.event.clearInstanceListeners(map);
        },
      };
    },

    directionsUrl,

    async searchPlaces(query, near, { areasOnly = false } = {}) {
      const text = query.trim();
      if (text.length < 2) return [];
      await libraries();
      const { Place } = await importLibrary('places');
      const { places } = await Place.searchByText({
        textQuery: text, fields: ['id', 'displayName', 'formattedAddress', 'location', 'types', 'viewport'], maxResultCount: areasOnly ? 10 : 5,
        ...(near ? { locationBias: { center: near, radius: 30_000 } } : {}),
      });
      return places.filter(place => place.location).map(place => {
        const viewport = place.viewport?.toJSON() ?? null;
        return {
          name: place.displayName || place.formattedAddress || text,
          address: place.formattedAddress || '',
          point: { lat: place.location!.lat(), lng: place.location!.lng() },
          placeId: place.id,
          level: placeLevelFromTypes(place.types ?? []),
          viewport,
        };
      }).filter(place => !areasOnly || place.level);
    },

    async placeName(point) {
      await libraries();
      const { Geocoder } = await importLibrary('geocoding');
      const { results } = await new Geocoder().geocode({ location: point });
      for (const result of results) {
        const area = areaFromComponents(result.address_components.map(component => ({ types: component.types, name: component.long_name })));
        if (area) return area;
      }
      return null;
    },

    async townAt(point) {
      await libraries();
      const { Geocoder } = await importLibrary('geocoding');
      const { results } = await new Geocoder().geocode({ location: point });
      for (const result of results) {
        const place = townFromComponents(result.address_components.map(component => ({ types: component.types, name: component.long_name, short: component.short_name })));
        if (place) return place;
      }
      return null;
    },

    async findTowns(query) {
      const text = query.trim();
      if (text.length < 2) return [];
      await libraries();
      const { Place } = await importLibrary('places');
      const { places } = await Place.searchByText({ textQuery: text, includedType: 'locality', fields: ['displayName', 'formattedAddress', 'addressComponents', 'location'], maxResultCount: 6 });
      return places.flatMap(place => {
        const found = townFromComponents((place.addressComponents ?? []).map(component => ({ types: component.types, name: component.longText ?? '', short: component.shortText ?? '' })));
        if (!found || !place.location) return [];
        return [{ ...found, label: place.formattedAddress || `${found.town}, ${found.country}`, point: { lat: place.location.lat(), lng: place.location.lng() } }];
      });
    },
  };
}
